import { useEffect, useState } from 'react'
import {Building2, Pencil, Plus, Search} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import CidadeModal from '@/components/cidades/CidadeModal'
import { formatDateBR } from '@/utils/masks'

function Cidades() {
  const { user } = useAppAuth()
  const isAdmin = user?.role === 'ADMIN'

  const [cidades, setCidades] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [busca, setBusca] = useState('')
  const [modalAberto, setModalAberto] = useState(false)
  const [cidadeEditando, setCidadeEditando] = useState<any>(null)

  const fetchCidades = async () => {
    setLoading(true)
    try {
      const { list } = await (lumi.entities as any).cidades.list({ sort: { nome: 1 } })
      setCidades(list || [])
    }
    catch {
      toast.error('Erro ao carregar cidades.')
    }
    finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCidades()
  }, [])

  // Administrador tem acesso total: nenhum filtro de cidade é aplicado, todas aparecem.
  // Moderador/Gestor não têm acesso a esta tela (gerida pela Sidebar/rota).
  const cidadesVisiveis = isAdmin ? cidades : cidades.filter(c => c._id === user?.cidadeId)

  const cidadesFiltradas = cidadesVisiveis.filter(c =>
    c.nome?.toLowerCase().includes(busca.toLowerCase())
    || c.nomeSistema?.toLowerCase().includes(busca.toLowerCase()),
  )

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-[20px] font-semibold text-[#12141A]">Cidades</h1>
          <p className="text-[13.5px] text-[#6B7280] mt-0.5">
            {isAdmin ? 'Gestão de todas as cidades do sistema (acesso total).' : 'Cidade vinculada ao seu perfil.'}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setCidadeEditando(null); setModalAberto(true) }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E4FBF] hover:bg-[#1A44A6] text-white text-[13.5px] font-medium"
          >
            <Plus className="w-4 h-4" strokeWidth={1.75} />
            Nova Cidade
          </button>
        )}
      </div>

      <div className="relative max-w-[360px]">
        <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
        <input
          value={busca}
          onChange={e => setBusca(e.target.value)}
          placeholder="Pesquisar cidade..."
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
        />
      </div>

      <div className="bg-white rounded-2xl border border-[#E7E9EC] overflow-hidden">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr className="border-b border-[#E7E9EC] bg-[#F9FAFB] text-left">
              <th className="px-5 py-3 font-medium text-[#6B7280]">Cidade</th>
              <th className="px-5 py-3 font-medium text-[#6B7280]">Nome do Sistema</th>
              <th className="px-5 py-3 font-medium text-[#6B7280]">Criada em</th>
              {isAdmin && <th className="px-5 py-3 font-medium text-[#6B7280] text-right">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-5 py-8 text-center text-[#9CA3AF]">A carregar...</td></tr>
            )}
            {!loading && cidadesFiltradas.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-[#9CA3AF]">Nenhuma cidade encontrada.</td></tr>
            )}
            {!loading && cidadesFiltradas.map(c => (
              <tr key={c._id} className="border-b border-[#F1F2F4] last:border-0 hover:bg-[#F9FAFB]">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-[#EEF3FE] flex items-center justify-center shrink-0 overflow-hidden">
                      {c.logoUrl
                        ? <img src={c.logoUrl} alt={c.nome} className="w-full h-full object-cover" />
                        : <Building2 className="w-4 h-4 text-[#1E4FBF]" strokeWidth={1.75} />}
                    </div>
                    <span className="font-medium text-[#12141A]">{c.nome}</span>
                  </div>
                </td>
                <td className="px-5 py-3.5 text-[#4B5563]">{c.nomeSistema || 'PROTOCOLEAI'}</td>
                <td className="px-5 py-3.5 text-[#4B5563]">{formatDateBR(c.createdAt)}</td>
                {isAdmin && (
                  <td className="px-5 py-3.5 text-right">
                    <button
                      onClick={() => { setCidadeEditando(c); setModalAberto(true) }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-medium text-[#374151] border border-[#D1D5DB] hover:bg-[#F5F6F8]"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                      Editar
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalAberto && (
        <CidadeModal
          cidade={cidadeEditando}
          onClose={() => setModalAberto(false)}
          onSaved={fetchCidades}
        />
      )}
    </div>
  )
}

export default Cidades
